import useKeyValueStorage from '@/hooks/storage/useKeyValueStorage';
import { getFromAPI } from '@/lib/api';
import {
	SpeciesAttributes,
	StrapiAPIResponseArray,
	convertStrapiSingleMediaOrArrayToMediaArray,
} from '@/lib/apiTypes';
import { isIterable } from '@/lib/array';
import Recorder from 'opus-recorder';
import { useCallback, useEffect, useMemo } from 'react';
import useSWR, { preload } from 'swr';

preload('/species', getFromAPI);

export const useSpeciesOnlyForContext = () => {
	// Store cached species in state
	const { value: cachedSpecies, setValue: setCachedSpecies } =
		useKeyValueStorage<
			StrapiAPIResponseArray<SpeciesAttributes> | undefined
		>('species', undefined);

	// Fetch from API
	const {
		data: remoteSpecies,
		error,
		isLoading,
	} = useSWR<StrapiAPIResponseArray<SpeciesAttributes>>(
		'/species',
		getFromAPI,
		{
			revalidateOnReconnect: true,
			revalidateIfStale: true,
			revalidateOnFocus: true,
			revalidateOnMount: true,
			errorRetryCount: 5,
			errorRetryInterval: 5000,
			fallbackData: cachedSpecies,
		},
	);

	// Update cache if remote data is different
	useEffect(() => {
		if (
			remoteSpecies &&
			JSON.stringify(remoteSpecies) !== JSON.stringify(cachedSpecies)
		) {
			setCachedSpecies(remoteSpecies);
		}
	}, [remoteSpecies, cachedSpecies, setCachedSpecies]);

	// Return species
	const species = useMemo(() => {
		const bestSpeciesMatch =
			cachedSpecies !== undefined ? cachedSpecies : remoteSpecies;
		// Check if the data is iterable (not an error object)
		if (bestSpeciesMatch && !isIterable(bestSpeciesMatch?.data)) {
			return undefined;
		}
		return bestSpeciesMatch;
	}, [cachedSpecies, remoteSpecies]);

	// Species sorted by name, alphabetically ascending
	const speciesSortedByName = useMemo(() => {
		if (!species?.data) return [];
		const sortedSpecies = [...species.data];

		sortedSpecies.sort((a, b) => {
			if (a.attributes.titleLong < b.attributes.titleLong) {
				return -1;
			} else if (a.attributes.titleLong > b.attributes.titleLong) {
				return 1;
			}
			return 0;
		});

		return sortedSpecies;
	}, [species]);

	// Find species by ID
	const findSpeciesById = useCallback(
		(id: number | undefined) => {
			if (species?.data)
				return species.data.find((species) => species.id === id);
			return undefined;
		},
		[species],
	);

	// Find species by slug
	const findSpeciesBySlug = (slug: string) => {
		if (species)
			return species.data.find(
				(species) => species.attributes.slug == slug,
			);
		return undefined;
	};

	// Map of main images indexed by species ID
	const mainImagesById = useMemo(() => {
		const mainImages: Map<
			number,
			{
				isAvailable: boolean;
				url: string | undefined;
				alt: string;
				caption: string;
				thumbnailUrl: string;
			}
		> = new Map();

		if (!species?.data) return mainImages;

		species?.data.forEach((species) => {
			const imagesFromSpecies = species.attributes.imageMain?.data;
			let image = undefined;
			if (imagesFromSpecies && Array.isArray(imagesFromSpecies))
				image = imagesFromSpecies[0];
			else if (imagesFromSpecies) image = imagesFromSpecies;

			mainImages.set(species.id, {
				isAvailable: !!image,
				url:
					image?.attributes.url ??
					image?.attributes.formats?.thumbnail.url,
				alt: image?.attributes.alternativeText ?? '',
				caption: image?.attributes.caption ?? '',
				thumbnailUrl:
					image?.attributes.formats?.thumbnail.url ??
					image?.attributes.url ??
					'',
			});
		});

		return mainImages;
	}, [species]);

	// Get main image of species by ID
	const getMainImageById = useCallback(
		(speciesId: number) => {
			return mainImagesById.get(speciesId ?? 0);
		},
		[mainImagesById],
	);

	// Map of routes indexed by species ID
	const routesById = useMemo(() => {
		const routes: Map<number, string> = new Map();

		if (!species?.data) return routes;

		species?.data.forEach((species) => {
			routes.set(
				species.id,
				`/soort/${species.id}-${species.attributes.slug}`,
			);
		});

		return routes;
	}, [species]);

	// Get species route by ID
	const getSpeciesRouteById = useCallback(
		(speciesId: number) => {
			return routesById.get(speciesId ?? 0) ?? '/';
		},
		[routesById],
	);

	// Map of species that support audio recognition indexed by species ID
	const speciesSupportsAudioRecognitionById = useMemo(() => {
		const speciesSupportsAudioRecognition: Map<number, boolean> = new Map();

		if (!species?.data) return speciesSupportsAudioRecognition;

		species?.data.forEach((species) => {
			const isRecorderSupported = species.attributes.isRecorderSupported;
			const hasAudioSample = convertStrapiSingleMediaOrArrayToMediaArray(
				Object.values(species.attributes.speciesDescription)
					.filter((description) => typeof description !== 'number')
					.map((description) => description.media)
					.flat(),
			).some((media) => media.mime.includes('audio'));

			speciesSupportsAudioRecognition.set(
				species.id,
				!!isRecorderSupported && hasAudioSample,
			);
		});

		return speciesSupportsAudioRecognition;
	}, [species]);

	// Get if species supports audio recognition
	const getSpeciesSupportsAudioRecognition = useCallback(
		(speciesId: number) => {
			if (!speciesId || !Recorder.isRecordingSupported()) return false;

			return speciesSupportsAudioRecognitionById.get(speciesId);
		},
		[speciesSupportsAudioRecognitionById],
	);

	return {
		species,
		speciesSortedByName,
		findSpeciesById,
		findSpeciesBySlug,
		getMainImageById,
		getSpeciesRouteById,
		getSpeciesSupportsAudioRecognition,
		isLoading,
		error,
	};
};
